import { clone, groupBy } from 'ramda';
import { actionTypes } from '../actions';
import {
  CASE_SCHEDULE_OP_MODE,
  FLAG_VALUE,
  debugLogToConsole,
  padZero,
  ascendingSortProviderBlocksByStart,
  makeIsoDateString,
  calculateCaseScheduleDataFlag
} from '../utils';

const TODAY = new Date();
const START_DATE = new Date();
START_DATE.setDate(START_DATE.getDate() - 7);

// ____ Helper Function _____________________________________________________\
const markProviderConcurrency = (blocks) => {
  let lbStartHour = -1;
  let lbStartMinute = -1;
  let lbEndHour = -1;
  let lbEndMinute = -1;
  let lastIndex = -1;

  return (block, idx) => {
    const { startHour, startMinute, endHour, endMinute } = block;

    // First element
    if (lbStartHour === -1 && lbStartMinute === -1 && lbEndHour === -1 && lbEndMinute === -1) {
      lbStartHour = startHour;
      lbStartMinute = startMinute;
      lbEndHour = endHour;
      lbEndMinute = endMinute;
      lastIndex = idx;
      return;
    }
    // Rest of the provider case blocks
    if (startHour > lbEndHour) {
      block.isConcurrent = false;
    } else if (startHour === lbEndHour) {
      if (startMinute >= lbEndMinute) {
        block.isConcurrent = false;
      } else {
        block.isConcurrent = true;
        blocks[lastIndex].isConcurrent = true;
      }
    } else {
      block.isConcurrent = true;
      blocks[lastIndex].isConcurrent = true;
    }

    lbStartHour = startHour;
    lbStartMinute = startMinute;
    lbEndHour = endHour;
    lbEndMinute = endMinute;
    lastIndex = idx;
  };
};

const markProviderConcurrencyHandler = (blocks) => {
  const updatedBlocks = clone(blocks);
  const cf = markProviderConcurrency(updatedBlocks);
  updatedBlocks.forEach((bc, idx) => {
    cf(bc, idx);
  });

  return updatedBlocks;
};

const initialState = {
  // Schedule Grid date range fitler
  scheduleDate: localStorage.getItem('scheduleDate') ? +localStorage.getItem('scheduleDate') : START_DATE.getDate(),
  scheduleMonth: localStorage.getItem('scheduleMonth') ? +localStorage.getItem('scheduleMonth') : START_DATE.getMonth(),
  scheduleYear: localStorage.getItem('scheduleYear') ? +localStorage.getItem('scheduleYear') : START_DATE.getFullYear(),
  scheduleEndDate: localStorage.getItem('scheduleEndDate') ? +localStorage.getItem('scheduleEndDate') : TODAY.getDate(),
  scheduleEndMonth: localStorage.getItem('scheduleEndMonth') ? +localStorage.getItem('scheduleEndMonth') : TODAY.getMonth(),
  scheduleEndYear: localStorage.getItem('scheduleEndYear') ? +localStorage.getItem('scheduleEndYear') : TODAY.getFullYear(),
  selectedPractice: {},
  selectedPracticeIndex: 0,
  isLoadingCaseData: false,
  lastUpdateTime: new Date().getTime(),
  opMode: CASE_SCHEDULE_OP_MODE.noOp,
  patientTabFlag: FLAG_VALUE.critical,
  payerTabFlag: FLAG_VALUE.critical,
  // --------------------
  caseTabFlag: FLAG_VALUE.critical,
  resyncCaseData: false,
  // --------------------
  newCaseSchedule: {},
  newCaseSchedulePatient: {},
  showNewCaseScheduleWarningDialog1: false,
  showNewCaseScheduleWarningDialog2: false,
  // --------------------
  caseSchedules: [],
  caseSchedulesCount: 0,
  facilities: [],
  userPractices: [],
  activeCaseSchedule: {},
  lastSavedCaseData: {},
  activeCaseSchedulePatientData: {},
  showCaseLoader: false,
  // --------------------
  showCbLoader: false,
  isEditingCaseBlock: false,
  shouldReCalculateBlockTime: false,
  showCaseBlockDrawer: false,
  showScheduleCaseDrawer: false,
  showCbCaseScheduleDashboard: false,
  reloadCaseBlocksData: false,
  reloadOrphanedCaseId: '',
  activeCaseBlock: {},
  caseBlockCaseSchedules: [],
  caseBlockPatients: {},
  caseBlockCasesMarkedForCreation: [],
  caseBlockCasesMarkedForDeletion: [],
  caseBlockActiveCaseSchedule: {},
  selectedCaseBlockDate: TODAY.getDate(),
  selectedCaseBlockMonth: TODAY.getMonth(),
  selectedCaseBlockYear: TODAY.getFullYear(),
  caseBlocks: [],
  practiceFacilities: [],
  practiceFacilitiesLocations: [],
  practiceCaseBlocks: [],
  practiceDirectorBlocks: [],
  practiceShiftBlocks: [],
  practiceCallBlocks: [],
  shouldSaveCaseData: false
};

export default function (state = initialState, action) {
  switch (action.type) {
    case actionTypes.setSelectedPractice:
      return {
        ...state,
        selectedPractice: state.userPractices[action.payload],
        selectedPracticeIndex: action.payload
      };
    case actionTypes.setSelectedCaseBlockDate: {
      const { year, month, date } = action.payload;
      return {
        ...state,
        selectedCaseBlockDate: date,
        selectedCaseBlockMonth: month,
        selectedCaseBlockYear: year
      };
    }
    // -------------------------------------------
    case actionTypes.setCaseTabFlag:
      return {
        ...state,
        caseTabFlag: action.payload
      };
    case actionTypes.setOpMode:
      return {
        ...state,
        opMode: action.payload
      };
    case actionTypes.setLoadingCaseData:
      return {
        ...state,
        isLoadingCaseData: action.payload
      };
    case actionTypes.shouldResyncCaseData:
      return {
        ...state,
        resyncCaseData: action.payload
      };
    // -------------------------------------------
    case actionTypes.initiateNewCaseSchedule: {
      const TODAY = new Date();
      const year = TODAY.getFullYear();
      const month = TODAY.getMonth();
      const date = TODAY.getDate();
      const facility = action.payload && action.payload.facility ? action.payload.facility : {};
      const selectedFacilityLocation = action.payload && action.payload.selectedFacilityLocation ? action.payload.selectedFacilityLocation : '';
      return {
        ...state,
        opMode: CASE_SCHEDULE_OP_MODE.newSchedule,
        newCaseSchedule: {
          facility: facility,
          selectedFacilityLocation,
          scheduleStartDate: makeIsoDateString(year, month, date),
          // scheduleStartDate: new Date().toISOString(),
          // scheduleEndDate: new Date().toISOString(),
          scheduleEndDate: makeIsoDateString(year, month, date),
          isNoneConsentSurgFormDocs: true,
          isNoneLabDocs: true,
          isNoneEkgDocs: true,
          isNoneImagingDocs: true,
          isNoneConsults: true,
          isNoneNursingDocs: true,
          isNoneNursingPreOpDocs: true,
          isNoneNursingIntraOpDocs: true,
          isNoneNursingPostOpDocs: true,
          isNoneSurgeonDocs: true,
          isNoneSurgeonPreOpDocs: true,
          isNoneSurgeonIntraOpDocs: true,
          isNoneSurgeonPostOpDocs: true,
          isNoneOrdersPreOpDocs: true,
          isNoneOrdersIntraOpDocs: true,
          isNoneOrdersPostOpDocs: true,
          isNoneDischargeDocs: true,
          showScheduleStartDayOfWeek: false,
          showScheduleEndDayOfWeek: false
        }
      };
    }
    case actionTypes.toggleNewCaseScheduleWarningDialog1:
      return {
        ...state,
        showNewCaseScheduleWarningDialog1: action.payload
      };
    case actionTypes.toggleNewCaseScheduleWarningDialog2:
      return {
        ...state,
        showNewCaseScheduleWarningDialog2: action.payload,
        opMode: CASE_SCHEDULE_OP_MODE.noOp,
        newCaseSchedule: {},
        newCaseSchedulePatient: {}
      };
    case actionTypes.updateNewCaseScheduleData: {
      const updatedNewCaseSchedule = Object.assign({}, state.newCaseSchedule);
      return {
        ...state,
        newCaseSchedule: {
          ...updatedNewCaseSchedule,
          ...action.payload,
          lastUpdateTime: new Date().getTime()
        }
      };
    }
    case actionTypes.createNewCaseSchedule:
      return {
        ...state,
        opMode: CASE_SCHEDULE_OP_MODE.noOp,
        newCaseSchedule: {},
        newCaseSchedulePatient: {},
        showNewCaseScheduleWarningDialog1: false,
        showNewCaseScheduleWarningDialog2: false
      };
    // -------------------------------------------
    case actionTypes.addPatientToNewCaseSchedule:
      return {
        ...state,
        newCaseSchedulePatient: action.payload
      };
    case actionTypes.updatePatientToNewCaseSchedule:
      return {
        ...state,
        newCaseSchedulePatient: action.payload
      };
    case actionTypes.deletePatientFromNewCaseSchedule:
      return {
        ...state,
        newCaseSchedulePatient: {}
      };
    case actionTypes.reloadCaseSchedulePatientData:
      return {
        ...state,
        newCaseSchedulePatient: action.payload
      };
    // -------------------------------------------
    case actionTypes.clearCaseSchedulesData:
      return {
        ...state,
        caseSchedules: []
      };
    case actionTypes.getCaseSchedulesData: {
      const {
        success,
        caseSchedules,
        caseSchedulesCount,
        facilities,
        userPractices,
        selectedPracticeNpi
      } = action.payload;

      if (success) {
        const spIndex = userPractices.findIndex(up => up.npi === +selectedPracticeNpi);
        return {
          ...state,
          caseSchedules,
          caseSchedulesCount,
          facilities,
          userPractices,
          selectedPractice: spIndex > -1 ? userPractices[spIndex] : {},
          isLoadingCaseData: false
        };
      }

      return {
        ...state,
        isLoadingCaseData: false
      };
    }
    case actionTypes.loadCaseSchedule: {
      return {
        ...state,
        showCaseLoader: false,
        opMode: CASE_SCHEDULE_OP_MODE.updatedSchedule,
        activeCaseSchedule: action.payload.caseScheduleData,
        lastSavedCaseData: action.payload.caseScheduleData,
        activeCaseSchedulePatientData: action.payload.patientData || {},
        caseTabFlag: calculateCaseScheduleDataFlag(action.payload.caseScheduleData)
      };
    }
    case actionTypes.toggleCaseLoader:
      return {
        ...state,
        showCaseLoader: action.payload
      };
    case actionTypes.updateCaseSchedule: {
      const updatedActiveCaseSchedule = clone(state.activeCaseSchedule);
      return {
        ...state,
        activeCaseSchedule: {
          ...updatedActiveCaseSchedule,
          ...action.payload
        }
      };
    }
    case actionTypes.getCaseSchedulePatientData:
      return {
        ...state,
        activeCaseSchedulePatientData: action.payload
      };
    case actionTypes.saveCaseSchedule: {
      return state;
      // return {
      //   ...state,
      //   activeCaseSchedule: clone(action.payload),
      //   lastSavedCaseData: clone(action.payload)
      // };
    }
    case actionTypes.collapseReload:
      return {
        ...state,
        opMode: CASE_SCHEDULE_OP_MODE.noOp,
        activeCaseSchedule: {},
        practiceCaseBlocks: [],
        activeCaseSchedulePatientData: {}
      };
    // -------------------------------------------
    case actionTypes.addPatientToExistingCaseSchedule:
      return {
        ...state,
        activeCaseSchedulePatientData: action.payload
      };
    case actionTypes.updatePatientToExistingCaseSchedule:
      return {
        ...state,
        activeCaseSchedulePatientData: action.payload
      };
    case actionTypes.deletePatientFromCaseSchedule: {
      const updatedActiveCaseSchedule = Object.assign({}, state.activeCaseSchedule);
      updatedActiveCaseSchedule.patientId = null;
      return {
        ...state,
        activeCaseSchedule: {
          ...updatedActiveCaseSchedule
        },
        activeCaseSchedulePatientData: {}
      };
    }
    case actionTypes.reloadExistingCaseSchedulePatientData:
      return {
        ...state,
        activeCaseSchedulePatientData: action.payload
      };
    // -------------------------------------------
    case actionTypes.setCaseBlockCaseInitialData:
      return {
        ...state,
        opMode: CASE_SCHEDULE_OP_MODE.caseBlockNewCase,
        caseBlockActiveCaseSchedule: action.payload
      };
    case actionTypes.setCaseBlockActiveCaseSchedule: {
      debugLogToConsole('🟠🟠🟠🟠🟠🟠🟠🟠');
      const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      const caseIdx = updatedCaseBlockCaseSchedules.findIndex(el => el._id === action.payload);
      debugLogToConsole(`Case Index = ${caseIdx}`);
      if (caseIdx > -1) {
        debugLogToConsole(updatedCaseBlockCaseSchedules[caseIdx]);
        return {
          ...state,
          caseBlockActiveCaseSchedule: updatedCaseBlockCaseSchedules[caseIdx]
        };
      }
      return state;
    }
    case actionTypes.toggleCbCaseScheduleDashboard: {
      const { value, caseId } = action.payload;
      if (caseId) {
        debugLogToConsole('🟠🟠🟠🟠🟠🟠🟠🟠');
        const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
        const caseIdx = updatedCaseBlockCaseSchedules.findIndex(el => el._id === caseId);
        debugLogToConsole(`Case Index = ${caseIdx}`);
        if (caseIdx > -1) {
          debugLogToConsole(updatedCaseBlockCaseSchedules[caseIdx]);
          return {
            ...state,
            caseBlockActiveCaseSchedule: updatedCaseBlockCaseSchedules[caseIdx],
            showCbCaseScheduleDashboard: value
          };
        }
        return {
          ...state,
          showCbCaseScheduleDashboard: value
        };
      }
      return {
        ...state,
        showCbCaseScheduleDashboard: value
      };
    }
    case actionTypes.updateCaseBlockActiveCaseSchedule: {
      const updatedCaseBlockActiveCaseSchedule = clone(state.caseBlockActiveCaseSchedule);
      return {
        ...state,
        caseBlockActiveCaseSchedule: {
          ...updatedCaseBlockActiveCaseSchedule,
          ...action.payload
        }
      };
    }
    case actionTypes.markCaseBlockCaseForCreation: {
      const updatedCaseBlockActiveCaseSchedule = clone(state.caseBlockActiveCaseSchedule);
      if (state.isEditingCaseBlock) {
        updatedCaseBlockActiveCaseSchedule.isNew = true;
      }
      let updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      updatedCaseBlockCaseSchedules = updatedCaseBlockCaseSchedules.concat(updatedCaseBlockActiveCaseSchedule);
      return {
        ...state,
        caseBlockCaseSchedules: updatedCaseBlockCaseSchedules,
        caseBlockActiveCaseSchedule: {},
        shouldReCalculateBlockTime: updatedCaseBlockCaseSchedules.length > 1
      };
    }
    case actionTypes.editCaseBlockCaseMarkedForCreation: {
      const updatedCaseBlockActiveCaseSchedule = clone(action.payload);
      // updatedCaseBlockActiveCaseSchedule.tmpId = Math.random().toString(36).slice(2);
      return {
        ...state,
        caseBlockActiveCaseSchedule: updatedCaseBlockActiveCaseSchedule
      };
    }
    case actionTypes.updateCaseBlockCaseMarkedForCreation: {
      const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      const tmpIdx = updatedCaseBlockCaseSchedules.findIndex((el) => {
        return el.tmpId && el.tmpId === state.caseBlockActiveCaseSchedule.tmpId;
      });
      if (tmpIdx > -1) {
        const tmpCaseData = clone(state.caseBlockActiveCaseSchedule);
        // delete tmpCaseData.tmpId;
        updatedCaseBlockCaseSchedules[tmpIdx] = tmpCaseData;
        return {
          ...state,
          caseBlockCaseSchedules: updatedCaseBlockCaseSchedules,
          caseBlockActiveCaseSchedule: {},
          shouldReCalculateBlockTime: updatedCaseBlockCaseSchedules.length > 1
        };
      }
      return state;
    }
    case actionTypes.resetCaseBlockLinkTimeFlag:
      return {
        ...state,
        shouldReCalculateBlockTime: false
      };
    case actionTypes.unMarkCaseBlockCaseForCreation: {
      const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      updatedCaseBlockCaseSchedules.splice(action.payload, 1);
      return {
        ...state,
        caseBlockCaseSchedules: updatedCaseBlockCaseSchedules,
        shouldReCalculateBlockTime: true
      };
    }
    case actionTypes.markCaseForUpdate: {
      const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      const caseIdx = updatedCaseBlockCaseSchedules.findIndex(el => el._id === state.caseBlockActiveCaseSchedule._id);
      debugLogToConsole(`Case Index = ${caseIdx}`);
      if (caseIdx > -1) {
        updatedCaseBlockCaseSchedules[caseIdx] = clone(state.caseBlockActiveCaseSchedule);
        debugLogToConsole(updatedCaseBlockCaseSchedules[caseIdx]);
        return {
          ...state,
          caseBlockCaseSchedules: updatedCaseBlockCaseSchedules,
          caseBlockActiveCaseSchedule: {}
        };
      }
      return state;
    }
    case actionTypes.markCaseBlockCaseForDeletion: {
      const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      const csIdx = updatedCaseBlockCaseSchedules.findIndex(el => el._id === action.payload);
      if (csIdx > -1) {
        updatedCaseBlockCaseSchedules[csIdx].isMarkedForDeletion = true;
      }
      return {
        ...state,
        caseBlockCaseSchedules: updatedCaseBlockCaseSchedules,
        shouldReCalculateBlockTime: true
      };
    }
    case actionTypes.unMarkCaseBlockCaseForDeletion: {
      const updatedCaseBlockCaseSchedules = clone(state.caseBlockCaseSchedules);
      const csIdx = updatedCaseBlockCaseSchedules.findIndex(el => el._id === action.payload);
      if (csIdx > -1) {
        updatedCaseBlockCaseSchedules[csIdx].isMarkedForDeletion = false;
      }
      return {
        ...state,
        caseBlockCaseSchedules: updatedCaseBlockCaseSchedules,
        shouldReCalculateBlockTime: true
        // caseBlockActiveCaseSchedule: {}
      };
    }
    case actionTypes.toggleCbLoader:
      debugLogToConsole(`toggleCbLoader ${action.payload}`);
      return {
        ...state,
        showCbLoader: action.payload
      };
    case actionTypes.toggleScheduleCaseDrawer:
      return {
        ...state,
        showScheduleCaseDrawer: action.payload
      };
    case actionTypes.initiateNewCaseBlock:
      return {
        ...state,
        isEditingCaseBlock: false,
        caseBlockCaseSchedules: [],
        activeCaseBlock: {
          ...action.payload
        }
      };
    case actionTypes.updateCaseBlockData:
      return {
        ...state,
        activeCaseBlock: {
          ...state.activeCaseBlock,
          ...action.payload
        }
      };
    case actionTypes.saveCaseBlock:
      return {
        ...state,
        // practiceCaseBlocks: [],
        practiceFacilities: [],
        practiceFacilitiesLocations: [],
        practiceCaseBlocks: [],
        practiceDirectorBlocks: [],
        practiceShiftBlocks: [],
        practiceCallBlocks: [],
        showCaseBlockDrawer: false,
        reloadCaseBlocksData: true
      };
    case actionTypes.toggleCaseBlockDrawer:
      return {
        ...state,
        showCaseBlockDrawer: action.payload,
        activeCaseBlock: {
          ...state.activeCaseBlock,
          isBlockStartTimePristine: null,
          isBlockEndTimePristine: null
        }
      };
    case actionTypes.loadCaseBlocksData: {
      const {
        facilities,
        assignments,
        caseBlocks,
        patientsData,
        orphanedCases,
        directorBlocks,
        shiftBlocks,
        callBlocks
      } = action.payload;

      // Facility Locations
      const facData = [];
      let locData = [];
      facilities.forEach((facility) => {
        const add1 = facility.mailingAddress.streetAddress1;
        const suite = facility.mailingAddress.suite;
        const city = facility.mailingAddress.city;
        const state = facility.mailingAddress.state;
        const zip = facility.mailingAddress.zip;

        facData.push({
          id: facility._id || facility.id,
          facilityName: `${facility.displayName}|${facility.avatarImageUrl}`,
          displayName: facility.displayName,
          userName: facility.userName,
          mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
          phone: facility.phone ? facility.phone.replace(/-/g, '') : '',
          taxonomy: facility.taxonomy,
          locations: facility.locations,
          bgColor: facility.bgColor,
          textColor: facility.textColor
        });
      });

      facData.forEach(facility => {
        if (facility.locations && Array.isArray(facility.locations) && facility.locations.length > 0) {
          facility.locations.forEach((location) => {
            locData.push({
              id: `${facility.id}__${location}`,
              displayName: facility.displayName,
              facilityId: facility.id,
              facilityName: facility.displayName,
              facilityUserName: facility.userName,
              location,
              bgColor: facility.bgColor,
              textColor: facility.textColor,
              surgeons: state.selectedPractice.users.filter((el) => {
                if (el.specialty) {
                  return el.specialty.search(/surg/ig) > -1;
                }
                return false;
              }),
              anesthesiologists: state.selectedPractice.users.filter((el) => {
                if (el.specialty) {
                  return el.specialty.search(/anes/ig) > -1;
                }
                return false;
              }),
              crnas: state.selectedPractice.users.filter((el) => {
                if (el.specialty) {
                  return el.specialty.search(/crna/ig) > -1;
                }
                return false;
              })
            });
          });
        }
      });

      // Add assignments
      if (assignments && Array.isArray(assignments) && assignments.length > 0) {
        locData = assignments.concat(locData);
      }

      const tmpOrphanedCases = orphanedCases.map(oc => ({
        _id: oc._id,
        anesthesiologist: oc.anesthesiologists[0],
        isOrphanedCase: true,
        surgeon: oc.surgeons[0],
        crna: oc.crnas[0],
        blockStartDate: oc.scheduleStartDate,
        blockStartHour: oc.scheduleStartHour,
        blockStartMinute: oc.scheduleStartMinute,
        blockEndDate: oc.scheduleEndDate,
        blockEndHour: oc.scheduleEndHour,
        blockEndMinute: oc.scheduleEndMinute,
        cases: [oc._id],
        surgicalProcedures: oc.surgicalProcedures,
        otherSurgicalProcedures: oc.otherSurgicalProcedures,
        date: oc.dateOfSurgery,
        facilityId: oc.facility.facility,
        facilityName: oc.facility.facilityName,
        location: oc.selectedFacilityLocation,
        isLinked: false,
        patientData: oc.patientData || null
      }));

      debugLogToConsole('loadCaseBlocksData false');
      return {
        ...state,
        showCbLoader: false,
        practiceFacilities: facData,
        practiceFacilitiesLocations: locData,
        practiceCaseBlocks: caseBlocks.concat(tmpOrphanedCases),
        caseBlockPatients: patientsData,
        practiceDirectorBlocks: directorBlocks,
        practiceShiftBlocks: shiftBlocks,
        practiceCallBlocks: callBlocks,
        reloadCaseBlocksData: false
      };
    }
    case actionTypes.createNewCaseBlock:
      return {
        ...state,
        showCaseBlockDrawer: false,
        reloadCaseBlocksData: true
      };
    case actionTypes.editCaseBlock: {
      const cbIndex = state.practiceCaseBlocks.findIndex(el => el._id === action.payload.cbId);
      if (cbIndex > -1) {
        const updatedActiveCaseBlock = clone(state.practiceCaseBlocks[cbIndex]);
        if (updatedActiveCaseBlock.isLinked) {
          updatedActiveCaseBlock.isBlockStartTimePristine = true;
          updatedActiveCaseBlock.isBlockEndTimePristine = true;
        } else {
          updatedActiveCaseBlock.isBlockStartTimePristine = false;
          updatedActiveCaseBlock.isBlockEndTimePristine = false;
        }
        return {
          ...state,
          showCaseBlockDrawer: true,
          // activeCaseBlock: clone(state.practiceCaseBlocks[cbIndex]),
          activeCaseBlock: updatedActiveCaseBlock,
          caseBlockCaseSchedules: action.payload.cbCases,
          isEditingCaseBlock: true
        };
      } else {
        return state;
      }
    }
    case actionTypes.setEditCaseBlockCases:
      return {
        ...state,
        caseBlockCaseSchedules: action.payload
      };
    case actionTypes.addPatientToCaseBlock: {
      const { _id } = action.payload;
      const updatedCaseBlockPatients = clone(state.caseBlockPatients);
      updatedCaseBlockPatients[_id] = action.payload;

      const updatedCaseBlockActiveCaseSchedule = clone(state.caseBlockActiveCaseSchedule);
      updatedCaseBlockActiveCaseSchedule.patientId = _id;
      return {
        ...state,
        caseBlockActiveCaseSchedule: updatedCaseBlockActiveCaseSchedule,
        caseBlockPatients: updatedCaseBlockPatients
      };
    }
    case actionTypes.reloadCaseBlockCasePatientData: {
      const { _id: patientId } = action.payload;
      if (patientId) {
        const updatedCaseBlockPatients = clone(state.caseBlockPatients);
        updatedCaseBlockPatients[patientId] = action.payload;
        return {
          ...state,
          caseBlockPatients: updatedCaseBlockPatients
        };
      } else {
        return state;
      }
    }
    case actionTypes.deletePatientFromCaseBlock: {
      const updatedCaseBlockPatients = clone(state.caseBlockPatients);
      delete updatedCaseBlockPatients[action.payload];

      const updatedCaseBlockActiveCaseSchedule = clone(state.caseBlockActiveCaseSchedule);
      updatedCaseBlockActiveCaseSchedule.patientId = null;

      return {
        ...state,
        caseBlockActiveCaseSchedule: updatedCaseBlockActiveCaseSchedule,
        caseBlockPatients: updatedCaseBlockPatients
      };
    }
    case actionTypes.getProvidersSchedule: {
      const practiceUsers = clone(state.selectedPractice.users);
      const updatedSelectedPractice = clone(state.selectedPractice);
      const updatedPracticeFacilities = clone(state.practiceFacilities);

      const {
        caseSchedules,
        directorBlocks,
        callBlocks,
        shiftBlocks
      } = action.payload;

      practiceUsers.forEach((pUser) => {
        const pCases = caseSchedules.filter((el) => {
          let match = false;
          for (let i = 0; i < el.anesthesiologists.length; i++) {
            if (el.anesthesiologists[i].npi === pUser.npi) {
              match = true;
              break;
            }
          }

          for (let i = 0; i < el.crnas.length; i++) {
            if (el.crnas[i].npi === pUser.npi) {
              match = true;
              break;
            }
          }

          return match;
        });
        const pDirBlocks = directorBlocks.filter((el) => el.director.npi === pUser.npi);
        const pCallBlocks = callBlocks.filter((el) => el.provider.npi === pUser.npi);
        const pShiftBlocks = shiftBlocks.filter((el) => el.provider.npi === pUser.npi);

        let allData = [];

        // ** CASE Block **
        const groupByFacilityUsername = groupBy(function (cs) {
          return cs.facility.facilityUserName;
        });
        const casesList = groupByFacilityUsername(pCases);
        const facs = Object.keys(casesList);
        const userCasesList = [];
        if (facs.length > 0) {
          facs.forEach((fac) => {
            let providerStartHour = 23;
            let providerStartMinute = 59;
            let providerEndHour = 0;
            let providerEndMinute = 0;

            casesList[fac].forEach((cs) => {
              providerStartHour = Math.min(providerStartHour, cs.scheduleStartHour);
              providerStartMinute = Math.min(providerStartMinute, cs.scheduleStartMinute);
              providerEndHour = Math.max(providerEndHour, cs.scheduleEndHour);
              providerEndMinute = Math.max(providerEndMinute, cs.scheduleEndMinute);
            });

            if (providerEndHour >= providerStartHour) {
              userCasesList.push({
                id: `Cases@${fac}${padZero(providerStartHour)}${padZero(providerStartMinute)}-${padZero(providerEndHour)}${padZero(providerEndMinute)}`,
                text: `Cases @${fac} ${padZero(providerStartHour)}${padZero(providerStartMinute)} - ${padZero(providerEndHour)}${padZero(providerEndMinute)}`,
                type: 'caseBlock',
                isConcurrent: false,
                startHour: providerStartHour,
                startMinute: providerStartMinute,
                endHour: providerEndHour,
                endMinute: providerEndMinute
              });
            }
          });
        }
        allData.push(...userCasesList);

        // ** DIRECTOR Block **
        const groupDBlocksByFacilityUsername = groupBy(function (db) {
          return db.facilityUsername;
        });

        const dbList = groupDBlocksByFacilityUsername(pDirBlocks);
        const dFacs = Object.keys(dbList);
        const userDbList = [];
        if (dFacs.length > 0) {
          dFacs.forEach((fac, idx) => {
            let dbStartHour = 23;
            let dbStartMinute = 59;
            let dbEndHour = 0;
            let dbEndMinute = 0;

            dbList[fac].forEach((db) => {
              dbStartHour = Math.min(dbStartHour, db.startHour);
              dbStartMinute = Math.min(dbStartMinute, db.startMinute);
              dbEndHour = Math.max(dbEndHour, db.stopHour);
              dbEndMinute = Math.max(dbEndMinute, db.stopMinute);
            });

            if (dbEndHour >= dbStartHour) {
              userDbList.push({
                id: `Directing @${fac}${padZero(dbStartHour)}${padZero(dbStartMinute)}-${padZero(dbEndHour)}${padZero(dbEndMinute)}_${idx}`,
                text: `Directing @${fac} ${padZero(dbStartHour)}${padZero(dbStartMinute)} - ${padZero(dbEndHour)}${padZero(dbEndMinute)}`,
                type: 'dirBlock',
                isConcurrent: false,
                startHour: dbStartHour,
                startMinute: dbStartMinute,
                endHour: dbEndHour,
                endMinute: dbEndMinute
              });
            }
          });
        }
        allData.push(...userDbList);

        // ** CALL Block **
        const groupCBlocksByFacilityUsername = groupBy(function (db) {
          return db.facilityUsername;
        });

        const cbList = groupCBlocksByFacilityUsername(pCallBlocks);
        const cFacs = Object.keys(cbList);
        const userCbList = [];
        if (cFacs.length > 0) {
          cFacs.forEach((fac, idx) => {
            let cbStartHour = 23;
            let cbStartMinute = 59;
            let cbEndHour = 0;
            let cbEndMinute = 0;

            cbList[fac].forEach((db) => {
              cbStartHour = Math.min(cbStartHour, db.startHour);
              cbStartMinute = Math.min(cbStartMinute, db.startMinute);
              cbEndHour = Math.max(cbEndHour, db.stopHour);
              cbEndMinute = Math.max(cbEndMinute, db.stopMinute);
            });

            if (cbEndHour >= cbStartHour) {
              userCbList.push({
                id: `Call @${fac}${padZero(cbStartHour)}${padZero(cbStartMinute)}-${padZero(cbEndHour)}${padZero(cbEndMinute)}_${idx}`,
                text: `Call @${fac} ${padZero(cbStartHour)}${padZero(cbStartMinute)} - ${padZero(cbEndHour)}${padZero(cbEndMinute)}`,
                type: 'callBlock',
                isConcurrent: false,
                startHour: cbStartHour,
                startMinute: cbStartMinute,
                endHour: cbEndHour,
                endMinute: cbEndMinute
              });
            }
          });
        }
        allData.push(...userCbList);

        // ** SHIFT Block **
        const groupSBlocksByFacilityUsername = groupBy(function (db) {
          return db.facilityUsername;
        });

        const sbList = groupSBlocksByFacilityUsername(pShiftBlocks);
        const sFacs = Object.keys(sbList);
        const userSbList = [];
        if (sFacs.length > 0) {
          sFacs.forEach((fac, idx) => {
            let sbStartHour = 23;
            let sbStartMinute = 59;
            let sbEndHour = 0;
            let sbEndMinute = 0;

            sbList[fac].forEach((db) => {
              sbStartHour = Math.min(sbStartHour, db.startHour);
              sbStartMinute = Math.min(sbStartMinute, db.startMinute);
              sbEndHour = Math.max(sbEndHour, db.stopHour);
              sbEndMinute = Math.max(sbEndMinute, db.stopMinute);
            });

            if (sbEndHour >= sbStartHour) {
              userSbList.push({
                id: `Shift @${fac}${padZero(sbStartHour)}${padZero(sbStartMinute)}-${padZero(sbEndHour)}${padZero(sbEndMinute)}_${idx}`,
                text: `Shift @${fac} ${padZero(sbStartHour)}${padZero(sbStartMinute)} - ${padZero(sbEndHour)}${padZero(sbEndMinute)}`,
                type: 'shiftBlock',
                isConcurrent: false,
                startHour: sbStartHour,
                startMinute: sbStartMinute,
                endHour: sbEndHour,
                endMinute: sbEndMinute
              });
            }
          });
        }
        allData.push(...userSbList);

        allData = markProviderConcurrencyHandler(allData.sort(ascendingSortProviderBlocksByStart));
        // console.log('💎💎💎💎💎');
        // console.log(allData);
        pUser.casesList = allData.filter(el => el.type === 'caseBlock');
        pUser.dList = allData.filter(el => el.type === 'dirBlock');
        pUser.sList = allData.filter(el => el.type === 'callBlock');
        pUser.cList = allData.filter(el => el.type === 'shiftBlock');
      });

      updatedSelectedPractice.users = practiceUsers;
      const tmpIdx = updatedPracticeFacilities.findIndex(el => el.npi === updatedSelectedPractice.npi);
      if (tmpIdx > -1) {
        updatedPracticeFacilities[tmpIdx].users = practiceUsers;
      }

      return {
        ...state,
        selectedPractice: updatedSelectedPractice,
        practiceFacilities: updatedPracticeFacilities
      };
    }
    case actionTypes.resetCaseBlocksState:
      return {
        ...state,
        practiceFacilities: [],
        practiceFacilitiesLocations: [],
        practiceCaseBlocks: []
      };
    case actionTypes.updateCaseBlockAnesthesiologist:
      return {
        ...state,
        reloadCaseBlocksData: true
      };
    case actionTypes.updateCaseBlockCrna:
      return {
        ...state,
        reloadCaseBlocksData: true
      };
    case actionTypes.saveFacilityColors: {
      const { bgColor, textColor, facilityId } = action.payload;
      const updatedPracticeFacilitiesLocations = clone(state.practiceFacilitiesLocations);
      updatedPracticeFacilitiesLocations.forEach(el => {
        if (el.id.startsWith(facilityId)) {
          el.bgColor = bgColor;
          el.textColor = textColor;
        }
      });
      return {
        ...state,
        practiceFacilitiesLocations: updatedPracticeFacilitiesLocations
      };
    }
    case actionTypes.setCaseScheduleDateRangeFilterStartDate:
      return {
        ...state,
        scheduleDate: action.payload.date,
        scheduleMonth: action.payload.month,
        scheduleYear: action.payload.year
      };
    case actionTypes.setCaseScheduleDateRangeFilterEndDate:
      return {
        ...state,
        scheduleEndDate: action.payload.date,
        scheduleEndMonth: action.payload.month,
        scheduleEndYear: action.payload.year
      };
    case actionTypes.setShouldSaveCaseData:
      return {
        ...state,
        shouldSaveCaseData: action.payload
      };
    default:
      return state;
  }
}
